<template>
  <div class="todo-list">
    <BCard>
      <BRow class="">
        <BCol class="col-md-6">
          <h2 class="text-blue font-weight-bold m-0">
            {{ $t("to_do") }}
          </h2>
        </BCol>
        <BCol class="col-md-6 text-right">
          <BFormRadioGroup
              v-model="filterStatus"
              :options="filterOptions"
              buttons
              button-variant="outline-primary"
              size="sm"
              name="radio-btn-outline"
              @change="updateTableData"
          />
        </BCol>
      </BRow>
    </BCard>
    <BCard>
      <Table
          :fields="tableFields"
          :items="todos"
          :url="url"
          :body="{ status: filterStatus }"
          :updateData="updateTrigger"
          class="position-relative"
          hover
          responsive
          show-empty
          striped
      >
        <template #head()="scope">
          <div class="text-nowrap">{{ $t(scope.label) }}</div>
        </template>
        <template #cell(created_at)="data">
          <span style="text-wrap: nowrap">{{ data.value.substring(10, 0) }}</span>
        </template>
        <template #cell(time)="data">
          <span style="width: 60px">{{ data.item.created_at.substring(11, 16) }}</span>
        </template>
        <template #cell(company_name)="data">
          {{ companyName(data.item) }}
        </template>
        <template #cell(company_id)="data">
          <p class="badge badge-light-primary" style="width:55px;margin: 0" v-if="data.item.invoice_id == null">
            <span v-if="data.item.company_id">
              C-{{ data.item.company_id }}
            </span>
            <span v-else-if="data.item.action === 'new_investment_profile'" @click="navigateInvestmentProfile(data.item)">
              IPR-{{ getInvestmentProfileId(data.item) }}
            </span>
          </p>
          <BLink
              v-else
              :to="canOpenInvoice ? '/invoice/' + data.item.invoice_id : null"
              class="badge badge-light-primary"
              :tabindex="canOpenInvoice ? 0 : -1"
              :style="canOpenInvoice ? 'width:55px;' : 'width:55px; pointer-events: none; '">
            I-{{ data.item.invoice_id }}
          </BLink>
        </template>
        <template #cell(description)="data">
          <template v-if="data.item.description">
            <template v-if="data.item.action === 'referral_program_check'">
              <span class="text-info">{{ $t(data.item.description) }}</span>
            </template>
            <template v-else>
              {{ $t(data.item.description) }}
            </template>
          </template>
          <template v-else>-</template>
        </template>
        <template #cell(status)="data">
          <span v-if="data.value === 0">{{ $t("pending") }} </span>
          <span v-else-if="data.value === 1">{{ $t("completed") }}</span>
          <span v-else-if="data.value === 2">{{ $t("closed") }}</span>
        </template>
        <template #cell(actions)="data">
          <template v-if="data.item.company_id">
            <template v-if="data.item.action === 'warning' && data.item.status === 0 && canManageTodo">
              <div class="todo-warning-actions">
                <BButton
                    :title="$t('settle_invoice')"
                    @click="settleInvoice(data.item)"
                    :disabled="buttons_disabled"
                    size="sm"
                    class="mr-1"
                    variant="outline-warning"
                    v-b-tooltip.hover
                >
                  <FeatherIcon icon="CheckCircleIcon"/>
                </BButton>
                <BButton
                    :title="$t('trigger_warning')"
                    @click="triggerWarning(data.item)"
                    :disabled="buttons_disabled"
                    size="sm"
                    variant="outline-success"
                    v-b-tooltip.hover
                >
                  <FeatherIcon icon="InfoIcon"/>
                </BButton>
              </div>
            </template>
            <template v-else-if="data.item.invoice_id != null">
              <BButton :to="'/invoice/' + data.item.invoice_id"  size="sm" variant="outline-dark">
                <FeatherIcon icon="CheckCircleIcon"/>
              </BButton>
            </template>
            <template v-else>
              <BButton @click="redirect(data.item)"  size="sm" v-if="data.item.status === 0 && data.item.action !== 'warning'" variant="outline-dark">
                <FeatherIcon icon="CheckCircleIcon"/>
              </BButton>
            </template>
          </template>
          <template v-else-if="data.item.action === 'referral_program_check'">
            <BButton @click="navigateReferralProgram" size="sm" variant="outline-info">
              <FeatherIcon icon="CheckCircleIcon"/>
            </BButton>
          </template>
          <template v-else-if="data.item.action === 'new_investment_profile'">
            <BButton @click="navigateInvestmentProfile(data.item)" size="sm" variant="outline-dark">
              <FeatherIcon icon="CheckCircleIcon"/>
            </BButton>
          </template>
          <template v-else>
            <BButton @click="navigateToSendEmail" size="sm" variant="outline-dark">
              <FeatherIcon icon="CheckCircleIcon"/>
            </BButton>
          </template>
        </template>
        <template #cell(change_status)="data">
          <template v-if="data.item.action !== 'referral_program_check'">
            <template v-if="data.item.status === 0">
              <BButton :title="$t('set_completed')" @click="setCompleted(data.item)" :disabled="buttons_disabled" class="mr-1" size="sm" v-b-tooltip.hover v-if="canManageTodo" variant="outline-success">
                <FeatherIcon icon="CheckCircleIcon"/>
              </BButton>
              <BButton :title="$t('set_closed')" @click="setClosed(data.item)" :disabled="buttons_disabled" size="sm" v-b-tooltip.hover v-if="data.item.action !== 'new_investment_profile' && canCloseTodo" variant="outline-danger">
                <FeatherIcon icon="XIcon"/>
              </BButton>
            </template>
            <BButton  :title="$t('set_pending')" @click="setPending(data.item)" :disabled="buttons_disabled"  size="sm" v-b-tooltip.hover v-if="data.item.status === 2 && canManageTodo" variant="outline-warning">
              <FeatherIcon icon="RefreshCwIcon"/>
            </BButton>
          </template>
        </template>
      </Table>
    </BCard>
  </div>
</template>

<script>
import {BButton, BCol, BLink, BRow, BFormRadioGroup} from "bootstrap-vue";
import Table from "@/components/Common/Table2";
import Ripple from "vue-ripple-directive";

export default {
  components: {
    Table,
    BRow,
    BCol,
    BButton,
    BLink,
    BFormRadioGroup
  },
  directives: {
    Ripple,
  },
  computed: {
    canOpenInvoice() {
      return this.adminType(['super_admin', 'admin_type_a']);
    },
    canManageTodo() {
      return this.adminType(['super_admin', 'admin_type_a']);
    },
    canCloseTodo() {
      return this.adminType(['super_admin']);
    },
    rows() {
      return this.totalItems;
    },
    tableFields() {
      const fields = [
        {
          key: "created_at",
          label: "Date",
          thClass: "todo-th-date",
          tdClass: "todo-td-date",
        },
        {
          key: "time",
          label: "Time",
          thClass: "todo-th-time",
          tdClass: "todo-td-time",
        },
        {
          key: "company_id",
          label: "ID",
        },
        {
          key: "company_name",
          label: "Company Name",
        },
        {
          key: "description",
          label: "description",
        },
        {
          key: "status",
          label: "status",
        },
      ];

      if (this.adminType(['super_admin', 'admin_type_a'])) {
        fields.push({
              key: "actions",
              label: "to_do",
            },
            {
              key: "change_status",
              label: "change_status",
            },
        );
      }

      return fields;
    }
  },
  data() {
    return {
      page: 1,
      totalItems: 0,
      url: '/task/todo',
      buttons_disabled: false,
      todos: null,
      filterStatus: 0,
      filterOptions: [
        { text: 'Pending', value: 0 },
        { text: 'All', value: 'all' }
      ],
      updateTrigger: 0,
      parsedDataCache: {},
    };
  },
  methods: {
    updateTableData() {
      this.updateTrigger++;
    },
    navigateToSendEmail() {
      this.$router.push('/send/email');
    },

    navigateReferralProgram() {
      this.$router.push('/referral-program');
    },

    navigateInvestmentProfile(item) {
      const id = this.getInvestmentProfileId(item);
      if (!id) return;
      this.$router.push(`/admin-investment-profile/${id}`);
    },

    decodeData(data) {
      try {
        return typeof data === 'string' ? JSON.parse(data) : data;
      } catch (e) {
        return {};
      }
    },
    getItemData(item) {
      if (!item || !item.id) return this.decodeData(item?.data);
      if (!this.parsedDataCache[item.id]) {
        this.parsedDataCache[item.id] = this.decodeData(item.data);
      }
      return this.parsedDataCache[item.id];
    },
    getInvestmentProfileId(item) {
      return this.getItemData(item)?.investment_profile_id || "-";
    },
    companyName(item) {
      if (item?.to_do_company?.name) return item.to_do_company.name;
      if (item?.to_do_company?.main_contact_person) {
        const contact = item.to_do_company.main_contact_person;
        return `${contact.name} ${contact.last_name}`;
      }
      if (item?.action === "new_investment_profile") {
        return this.getItemData(item)?.company_name || "";
      }
      return "";
    },


    redirect(item) {
      if (item.action === 'transactions' && item.to_do_company.main_contact_person) {
        this.$router.push(`/investor/${item.to_do_company.main_contact_person.id}#transactions`)
      } else if (['activate_invoice', 'debtor_confirmation', 'settle_reverse_invoice'].includes(item.action)) {
        let invoice = this.getItemData(item);
        this.$router.push(`/invoice/${invoice.invoice_id}`);
      } else if (item.action === 'verify_user_answers_from_sumsub' && item.to_do_company.main_contact_person) {
        this.$router.push(`/investor/${item.to_do_company.main_contact_person.id}`);
      } else if (['check_debtor_score','check_annual_fee', 'client_funds_50_day_warning', 'client_funds_60_day_escalation'].includes(item.action)) {
        this.$router.push(`/company/${item.company_id}`);
      } else if (item.action === "currency_exchange" || item.action === "annual_fee_exchange" ) {
        this.$router.push(`/transaction-currency`);
      } else if (item.action === "framework_agreement_admin_sign"){
        const url = this.getItemData(item)?.admin_signing_url
        if (!url) return;
        window.open(url, '_blank')
      }
    },
    setClosed(item) {
      this.updateTodoStatus(item, "closed");
    },
    setPending(item) {
      this.updateTodoStatus(item, "pending");
    },
    setCompleted(item) {
      this.updateTodoStatus(item, "finished");
    },
    updateTodoStatus(item, status) {
      this.buttons_disabled = true;
      this.$http.post(`/task/todo/${item.id}/${status}`).then((res) => {
        if (res) {
          this.showSuccessMessage();
        }
      }).finally(() => {
        this.buttons_disabled = false;
      });
    },
    getWarningInvoiceId(item) {
      return item.invoice_id || this.getItemData(item)?.invoice_id;
    },
    triggerWarning(item) {
      const invoiceId = this.getWarningInvoiceId(item);
      if (!invoiceId) return;

      this.$swal({
        title: this.$t('are_you_sure'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('trigger_warning'),
        cancelButtonText: this.$t('cancel'),
        customClass: {
          confirmButton: 'btn btn-warning',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (!result.isConfirmed) return;

        this.buttons_disabled = true;
        this.$http.post(`/invoice/admin/changeStatus/${invoiceId}`, {
          action: 'trigger_warning',
          todo_id: item.id,
        }).then((res) => {
          if (res?.data?.status === 200) {
            this.showSuccessMessage();
          }
        }).finally(() => {
          this.buttons_disabled = false;
        });
      });
    },
    settleInvoice(item) {
      const invoiceId = this.getWarningInvoiceId(item);
      if (!invoiceId) return;

      this.$swal({
        title: this.$t('are_you_sure'),
        text: this.$t('settle_invoice'),
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('settle_invoice'),
        cancelButtonText: this.$t('cancel'),
        customClass: {
          confirmButton: 'btn btn-success',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (!result.isConfirmed) return;

        this.$http.post(`/invoice/admin/changeStatus/${invoiceId}`, { status: 11 }).then((res) => {
          if (res?.data?.status === 200) {
            this.setCompleted(item);
          }
        });
      });
    },
    showSuccessMessage() {
      let text = this.$t("success");
      this.$swal({
        position: "center",
        icon: "success",
        title: text,
        showConfirmButton: false,
        timer: 1500,
        customClass: {
          confirmButton: "btn btn-primary",
        },
        buttonsStyling: false,
      });
      this.updateTableData();
    },
  },
};
</script>

<style scoped>
.todo-warning-actions {
  display: inline-flex;
  flex-wrap: nowrap;
  align-items: center;
  white-space: nowrap;
  gap: 0;
  min-width: max-content;
}
</style>

<style>
.todo-list th,
.todo-list td,
.todo-list th,
.todo-list td {
  padding: 0.72rem 1rem !important;
}
</style>